/* ---------------------------------------------------------------------------
   Shrink a photograph before it goes anywhere near the network.

   An inspector's phone takes 12-megapixel photographs of four or five megabytes
   each. The connection at the structure is one bar, and it is the constraint
   this whole application is designed around — so the upload is the wrong place
   to be honest about pixels. A 2048 px JPEG at quality 0.82 is a few hundred
   kilobytes, reads a crack just as well on any screen anyone will look at it on,
   and is the difference between an inspection that uploads from a riverbank and
   one that does not.

   **Everything comes out as JPEG.** These are photographs of concrete and steel,
   not diagrams: JPEG is what the camera produced and what it compresses well.
   Keeping PNG for PNG inputs would mean a screenshot pasted in by mistake
   uploading at ten times the size of a real photograph, which is the wrong way
   round. A PNG of a sketch will pick up some ringing; that is the trade, and it
   is the right one for what this field is for.

   **Orientation is baked in.** `createImageBitmap` is asked for
   `imageOrientation: 'from-image'`, so a photograph taken in portrait arrives
   the right way up rather than relying on an EXIF tag that the canvas drops and
   half the consumers of the file would ignore anyway.
   --------------------------------------------------------------------------- */

/**
 * Longest edge of an uploaded photograph, in pixels.
 *
 * 2048 is about a 3-megapixel image. Enough to zoom into a crack on a laptop
 * screen, small enough that eight of them are still a sane single request. A
 * photograph already smaller than this is never scaled UP — there is no more
 * detail to invent, and the bytes would grow for nothing.
 */
export const MAX_EDGE = 2048;

/** JPEG quality. High enough that the compression is not what you notice first. */
export const QUALITY = 0.82;

export interface Downscaled {
  /** `data:image/jpeg;base64,…`, ready to post. */
  dataUrl: string;
  width: number;
  height: number;
  /** Approximate encoded size in bytes, for the dialog to show. */
  bytes: number;
}

/**
 * Roughly how many kilobytes a data URL will send as, for the label under a
 * thumbnail.
 *
 * HERE RATHER THAN IN A DIALOG, where it was first written and then written again
 * the day a second form learned to attach a photograph. The two copies rounded
 * differently, which is a plain invitation for the same photograph to read as 312
 * kB on one form and 311 on the other.
 *
 * Shown at all because the size of an upload is the thing an inspector on one bar
 * of signal is actually deciding about, and because it is the only visible
 * evidence that the downscale below happened.
 */
export function approxKb(dataUrl: string): number {
  return Math.round(sizeOfDataUrl(dataUrl) / 1024);
}

/** Bytes behind a base64 data URL, without decoding it. */
function sizeOfDataUrl(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  if (comma === -1) return 0;
  const b64 = dataUrl.slice(comma + 1);
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((b64.length * 3) / 4) - padding);
}

/**
 * Read a picked file, shrink it, and hand back a data URL.
 *
 * Rejects with a message fit to put on screen: this runs on a file a person just
 * chose, and "that is not an image this can read" is something they can act on.
 */
export async function downscale(file: File, maxEdge: number = MAX_EDGE): Promise<Downscaled> {
  if (!file.type.startsWith('image/')) {
    throw new Error(`"${file.name}" is not an image.`);
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // A HEIC off an iPhone lands here on browsers that will not decode it, which
    // is worth saying plainly rather than as a decoder error.
    throw new Error(`"${file.name}" could not be read as an image by this browser.`);
  }

  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser would not provide a 2D canvas to resize the photograph.');
    ctx.drawImage(bitmap, 0, 0, width, height);

    const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
    return { dataUrl, width, height, bytes: sizeOfDataUrl(dataUrl) };
  } finally {
    // The decoded bitmap is the big allocation — several megabytes of RGBA — and
    // it is finished with the moment it has been drawn. Released whether the
    // encode succeeded or threw.
    bitmap.close();
  }
}
